/**
 * AssetBrowser - Browse and select maps, VFX, and Pokemon assets
 */

import React, { useState, useMemo, useCallback } from "react";
import { VFX_SPRITE_FRAME_COUNT_DATA } from "@/data/vfx-sprite-frame-count-data";

type AssetType = "map" | "vfx" | "pokemon";

interface AssetBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  type: AssetType;
  onSelect: (value: string) => void;
  currentValue?: string;
}

// Generate map list (1-65)
const MAP_LIST = Array.from({ length: 65 }, (_, i) => `map (${i + 1})`);

// Get VFX list from frame count data
const VFX_LIST = Object.keys(VFX_SPRITE_FRAME_COUNT_DATA);

// Common Pokemon list (can be expanded)
const POKEMON_LIST = [
  "pikachu",
  "charizard",
  "blastoise",
  "venusaur",
  "mewtwo",
  "mew",
  "dragonite",
  "gengar",
  "alakazam",
  "machamp",
  "gyarados",
  "lapras",
  "eevee",
  "vaporeon",
  "jolteon",
  "flareon",
  "snorlax",
  "articuno",
  "zapdos",
  "moltres",
  "ditto",
  "aerodactyl",
  "lucario",
  "garchomp",
  "rayquaza",
  "groudon",
  "kyogre",
  "dialga",
  "palkia",
  "giratina",
  "arceus",
  "darkrai",
  "metagross",
  "salamence",
  "tyranitar",
  "scizor",
  "umbreon",
  "espeon",
  "blaziken",
  "swampert",
  "sceptile",
  "infernape",
  "empoleon",
  "torterra",
  "luxray",
  "staraptor",
  "floatzel",
  "roserade",
  "gastrodon",
  "spiritomb",
  "togekiss",
  "glaceon",
  "leafeon",
  "sylveon",
  "zoroark",
  "hydreigon",
  "volcarona",
  "haxorus",
  "excadrill",
  "chandelure",
  "bisharp",
  "braviary",
  "mandibuzz",
  "golurk",
  "durant",
  "greninja",
  "talonflame",
  "aegislash",
  "dragalge",
  "clawitzer",
  "tyrantrum",
  "aurorus",
  "hawlucha",
  "goodra",
  "noivern",
  "decidueye",
  "incineroar",
  "primarina",
  "lycanroc",
  "mimikyu",
  "kommo-o",
  "tapu-koko",
  "tapu-lele",
  "tapu-bulu",
  "tapu-fini",
  "solgaleo",
  "lunala",
  "necrozma",
  "marshadow",
  "zeraora",
];

export const AssetBrowser: React.FC<AssetBrowserProps> = ({
  isOpen,
  onClose,
  type,
  onSelect,
  currentValue,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const getTitle = () => {
    switch (type) {
      case "map":
        return "Select Map";
      case "vfx":
        return "Select VFX";
      case "pokemon":
        return "Select Pokemon";
    }
  };

  const getAssetList = useCallback(() => {
    switch (type) {
      case "map":
        return MAP_LIST;
      case "vfx":
        return VFX_LIST;
      case "pokemon":
        return POKEMON_LIST;
    }
  }, [type]);

  // Group VFX by category
  const vfxCategories = useMemo(() => {
    if (type !== "vfx") return {};

    const categories: Record<string, string[]> = {};
    VFX_LIST.forEach((path) => {
      const parts = path.split("/");
      const category = parts[parts.length - 2] || "other";
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(path);
    });
    return categories;
  }, [type]);

  const filteredAssets = useMemo(() => {
    const assets = getAssetList();
    const query = searchQuery.toLowerCase();

    let filtered = assets.filter((asset) =>
      asset.toLowerCase().includes(query),
    );

    if (type === "vfx" && selectedCategory) {
      filtered = filtered.filter((path) =>
        path.includes(`/${selectedCategory}/`),
      );
    }

    return filtered;
  }, [getAssetList, searchQuery, type, selectedCategory]);

  const handleSelect = (asset: string) => {
    onSelect(asset);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-[800px] max-w-[90vw] h-[600px] max-h-[80vh] bg-[#1a1f2e] rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">{getTitle()}</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Search & Filters */}
        <div className="px-6 py-3 border-b border-gray-700 flex gap-4">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="flex-1 px-4 py-2 text-sm bg-gray-800 border border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none text-white"
          />

          {type === "vfx" && (
            <select
              value={selectedCategory || ""}
              onChange={(e) => setSelectedCategory(e.target.value || null)}
              className="px-4 py-2 text-sm bg-gray-800 border border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none text-white"
            >
              <option value="">All Categories</option>
              {Object.keys(vfxCategories).map((cat) => (
                <option key={cat} value={cat}>
                  {cat} ({vfxCategories[cat].length})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Asset Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {type === "map" && (
            <MapGrid
              assets={filteredAssets}
              currentValue={currentValue}
              onSelect={handleSelect}
            />
          )}
          {type === "vfx" && (
            <VFXGrid
              assets={filteredAssets}
              currentValue={currentValue}
              onSelect={handleSelect}
            />
          )}
          {type === "pokemon" && (
            <PokemonGrid
              assets={filteredAssets}
              currentValue={currentValue}
              onSelect={handleSelect}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-700 flex justify-between items-center">
          <span className="text-sm text-gray-400">
            {filteredAssets.length} items
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Grid Components
// ============================================================================

interface GridProps {
  assets: string[];
  currentValue?: string;
  onSelect: (asset: string) => void;
}

function MapGrid({ assets, currentValue, onSelect }: GridProps) {
  return (
    <div className="grid grid-cols-4 gap-3">
      {assets.map((mapId) => (
        <button
          key={mapId}
          onClick={() => onSelect(mapId)}
          className={`
            relative aspect-video rounded-lg overflow-hidden border-2 transition-all
            ${
              currentValue === mapId
                ? "border-blue-500 ring-2 ring-blue-500/50"
                : "border-transparent hover:border-gray-500"
            }
          `}
        >
          <img
            src={`/assets/maps/${mapId}.webp`}
            alt={mapId}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
            <span className="text-xs text-white font-medium">{mapId}</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function VFXGrid({ assets, currentValue, onSelect }: GridProps) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {assets.map((path) => {
        const fileName = path.split("/").pop() || path;
        const frameCount = VFX_SPRITE_FRAME_COUNT_DATA[path] || 1;

        return (
          <button
            key={path}
            onClick={() => onSelect(path)}
            className={`
              relative p-3 rounded-lg border-2 transition-all text-left
              ${
                currentValue === path
                  ? "border-blue-500 bg-blue-500/10"
                  : "border-gray-700 hover:border-gray-500 bg-gray-800/50"
              }
            `}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-900 rounded flex items-center justify-center overflow-hidden">
                <img
                  src={path}
                  alt={fileName}
                  className="max-w-full max-h-full object-contain"
                  style={{ imageRendering: "pixelated" }}
                  loading="lazy"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{fileName}</div>
                <div className="text-xs text-gray-500">{frameCount} frames</div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function PokemonGrid({ assets, currentValue, onSelect }: GridProps) {
  return (
    <div className="grid grid-cols-5 gap-3">
      {assets.map((pokemonName) => (
        <button
          key={pokemonName}
          onClick={() => onSelect(pokemonName)}
          className={`
            relative p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2
            ${
              currentValue === pokemonName
                ? "border-blue-500 bg-blue-500/10"
                : "border-gray-700 hover:border-gray-500 bg-gray-800/50"
            }
          `}
        >
          <div className="w-16 h-16 flex items-center justify-center">
            <img
              src={`https://pokegram-assets.s3.us-east-1.amazonaws.com/assets/pokemons/${pokemonName}/Idle-Anim.png`}
              alt={pokemonName}
              className="max-w-full max-h-full object-contain"
              style={{ imageRendering: "pixelated" }}
              loading="lazy"
              onError={(e) => {
                // Fallback if image doesn't load
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
          <span className="text-xs text-white capitalize truncate w-full text-center">
            {pokemonName}
          </span>
        </button>
      ))}
    </div>
  );
}



















